import { useState, useEffect } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Switch } from '../ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { ProviderModelSelector } from '../chat/provider-model-selector'
import { CRON_PRESETS, validateCron, describeCron, normalizeCronExpression } from '@/lib/cron-utils'
import { JobsApi, type CreateJobRequest, type ScheduledJob, type JobType } from '@/lib/jobs-api'
import { Loader2, X, AlertCircle } from 'lucide-react'

interface CreateJobDialogProps {
  isOpen: boolean
  onClose: () => void
  onCreated: (job: ScheduledJob) => void
  editJob?: ScheduledJob | null
  onUpdated?: (job: ScheduledJob) => void
}

const api = new JobsApi()

export function CreateJobDialog({
  isOpen,
  onClose,
  onCreated,
  editJob,
  onUpdated,
}: CreateJobDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [jobType, setJobType] = useState<JobType>('agent_task')
  const [schedule, setSchedule] = useState('0 * * * *')
  const [customSchedule, setCustomSchedule] = useState('')
  const [useCustomSchedule, setUseCustomSchedule] = useState(false)
  const [enabled, setEnabled] = useState(true)
  
  // Agent task specific
  const [provider, setProvider] = useState<string>('')
  const [model, setModel] = useState<string>('')
  const [prompt, setPrompt] = useState('')
  
  // System job specific
  const [command, setCommand] = useState('')
  const [args, setArgs] = useState('')

  const applyJobToForm = (job: ScheduledJob) => {
    setName(job.name)
    setDescription(job.description || '')
    setJobType(job.job_type as JobType)
    setSchedule(job.cron_expression)
    setEnabled(job.enabled)
    setProvider(job.provider || '')
    setModel(job.model || '')
    setPrompt(job.prompt || '')

    if (job.job_type === 'system_job') {
      const payload = job.payload && typeof job.payload === 'object'
        ? job.payload as Record<string, unknown>
        : undefined
      const payloadCommand = typeof payload?.command === 'string' ? payload.command : undefined
      const fallbackToolName = typeof job.tool_name === 'string' ? job.tool_name : ''
      setCommand(payloadCommand || fallbackToolName)

      const payloadArgs = payload?.args
      if (payloadArgs && typeof payloadArgs === 'object') {
        try {
          setArgs(JSON.stringify(payloadArgs, null, 2))
        } catch {
          setArgs('')
        }
      } else {
        setArgs('')
      }
    } else {
      setArgs('')
      setCommand('')
    }

    const cronValue = (job.cron_expression || '').trim()
    const isPreset = CRON_PRESETS.some(p => p.value === cronValue)
    setUseCustomSchedule(!isPreset)
    if (!isPreset) {
      setCustomSchedule(cronValue)
    } else {
      setCustomSchedule('')
    }
  }

  // Populate form when editing
  useEffect(() => {
    if (editJob) {
      applyJobToForm(editJob)
      let cancelled = false
      // Fetch canonical server copy to ensure fields like cron/payload are fresh.
      api.getJob(editJob.id)
        .then((freshJob) => {
          if (!cancelled) applyJobToForm(freshJob)
        })
        .catch(() => {})
      return () => {
        cancelled = true
      }
    } else {
      // Reset form for new job
      setName('')
      setDescription('')
      setJobType('agent_task')
      setSchedule('0 * * * *')
      setCustomSchedule('')
      setUseCustomSchedule(false)
      setEnabled(true)
      setProvider('')
      setModel('')
      setPrompt('')
      setCommand('')
      setArgs('')
    }
  }, [editJob, isOpen])

  const currentSchedule = useCustomSchedule ? customSchedule : schedule
  const scheduleValidation = validateCron(currentSchedule)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!scheduleValidation.valid) {
      setError(scheduleValidation.error || 'Invalid cron expression')
      return
    }

    const normalizedSchedule = normalizeCronExpression(currentSchedule)

    const jobData: CreateJobRequest = {
      name,
      description: description || undefined,
      job_type: jobType,
      cron_expression: normalizedSchedule,
      enabled,
    }

    if (jobType === 'agent_task') {
      if (!provider || !model || !prompt) {
        setError('Provider, model, and prompt are required for agent tasks')
        return
      }
      jobData.provider = provider
      jobData.model = model
      jobData.prompt = prompt
    } else {
      // System job - combine command and args into payload
      if (!command) {
        setError('Command is required for system jobs')
        return
      }
      const payload: Record<string, unknown> = { command }
      if (args) {
        try {
          payload.args = JSON.parse(args)
        } catch {
          setError('Args must be valid JSON')
          return
        }
      }
      jobData.payload = payload
    }

    setIsSubmitting(true)
    try {
      if (editJob) {
        const updated = await api.updateJob(editJob.id, jobData)
        onUpdated?.(updated)
      } else {
        const created = await api.createJob(jobData)
        onCreated(created)
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save job')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-job-dialog-title"
        data-testid="create-job-dialog"
        className="h-[92dvh] w-full overflow-y-auto border border-border bg-background text-foreground shadow-xl sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-lg"
      >
        <div className="flex items-center justify-between border-b px-4 py-3 sm:p-4">
          <h2 id="create-job-dialog-title" className="text-lg font-semibold sm:text-xl">
            {editJob ? 'Edit Job' : 'Create New Job'}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close dialog">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 p-4 sm:space-y-6">
          {/* Job Type Selection */}
          <Tabs value={jobType} onValueChange={(v) => setJobType(v as JobType)}>
            <TabsList className="grid h-auto w-full grid-cols-2">
              <TabsTrigger value="agent_task">Agent Task</TabsTrigger>
              <TabsTrigger value="system_job">System Job</TabsTrigger>
            </TabsList>

            {/* Agent Task Configuration */}
            <TabsContent value="agent_task" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label id="provider-model-label">Provider &amp; Model</Label>
                <ProviderModelSelector
                  provider={provider}
                  model={model || null}
                  onProviderChange={(next) => {
                    if (next !== provider) setModel('')
                    setProvider(next)
                  }}
                  onModelChange={(next) => setModel(next ?? '')}
                  className="w-full"
                />
              </div>

              <div>
                <Label htmlFor="prompt">Prompt</Label>
                <Textarea
                  id="prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="What should the agent do?"
                  rows={4}
                />
              </div>
            </TabsContent>

            {/* System Job Configuration */}
            <TabsContent value="system_job" className="space-y-4 mt-4">
              <div>
                <Label htmlFor="command">Command</Label>
                <Input
                  id="command"
                  value={command}
                  onChange={(e) => setCommand(e.target.value)}
                  placeholder="e.g., cleanup_old_files"
                />
              </div>

              <div>
                <Label htmlFor="args">Arguments (JSON)</Label>
                <Textarea
                  id="args"
                  value={args}
                  onChange={(e) => setArgs(e.target.value)}
                  placeholder='{"days": 30}'
                  rows={3}
                />
              </div>
            </TabsContent>
          </Tabs>

          {/* Common Fields */}
          <div className="border-t pt-4 space-y-4">
            <div>
              <Label htmlFor="name">Job Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My scheduled job"
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this job does (optional)"
                rows={2}
              />
            </div>

            {/* Schedule */}
            <div>
              <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <Label>Schedule</Label>
                <div className="flex items-center gap-2">
                  <Label htmlFor="custom-schedule" className="text-sm text-muted-foreground">
                    Custom
                  </Label>
                  <Switch
                    id="custom-schedule"
                    checked={useCustomSchedule}
                    onCheckedChange={setUseCustomSchedule}
                  />
                </div>
              </div>

              {useCustomSchedule ? (
                <div>
                  <Input
                    value={customSchedule}
                    onChange={(e) => setCustomSchedule(e.target.value)}
                    placeholder="* * * * *"
                    className={scheduleValidation.valid ? '' : 'border-red-500'}
                  />
                  {customSchedule && (
                    <p className={`text-sm mt-1 ${scheduleValidation.valid ? 'text-muted-foreground' : 'text-red-500'}`}>
                      {scheduleValidation.valid ? describeCron(customSchedule) : scheduleValidation.error}
                    </p>
                  )}
                </div>
              ) : (
                <Select value={schedule} onValueChange={setSchedule}>
                  <SelectTrigger data-testid="schedule-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CRON_PRESETS.map((preset) => (
                      <SelectItem key={preset.value} value={preset.value}>
                        <div className="flex flex-col">
                          <span>{preset.label}</span>
                          <span className="text-xs text-muted-foreground font-mono">
                            {preset.value}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Enabled */}
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label htmlFor="enabled">Enabled</Label>
                <p className="text-sm text-muted-foreground">
                  Job will run according to schedule
                </p>
              </div>
              <Switch
                id="enabled"
                checked={enabled}
                onCheckedChange={setEnabled}
              />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 p-3 rounded-md">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="grid grid-cols-1 gap-3 border-t pt-4 sm:flex sm:justify-end">
            <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editJob ? 'Save Changes' : 'Create Job'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
